import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Box, Typography, Paper, TextField, IconButton, Button, Chip, Tooltip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { fetchData } from "../../api/client";
import { getConversationById } from "../../api/client";
import { useRequests } from "../../hooks/useRequests";
import StatusChipSelector, { normalizeStatusInput } from "../StatusChipSelector";
import { botMessagesFromUraResponse, buildSimulatorTurnMessages, resolveSimulatorActiveSlotId } from "../../utils/chatbotSimulatorMessages";
import SimulatorTypingBubble from "./SimulatorTypingBubble";
import AgentSimulatorAttachment from "../Agents/AgentSimulatorAttachment";
import { resolveSimulationProjectId } from "../../utils/primaryProjectId";

export default function ChatbotSimulator({ userId, onNavigateToSlot, onActiveSlotChange }) {
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 32, y: 80 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [requestStatus, setRequestStatus] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const { updateMutation } = useRequests();
  const sendingRef = useRef(false);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    sendingRef.current = sending;
  }, [sending]);

  useEffect(() => {
    if (!expanded) return undefined;
    const container = messagesContainerRef.current;
    if (!container) return undefined;

    const scrollToLatest = () => {
      container.scrollTop = container.scrollHeight;
    };

    scrollToLatest();
    const frame = requestAnimationFrame(scrollToLatest);
    return () => cancelAnimationFrame(frame);
  }, [messages, expanded, sending]);

  // Ao carregar a tela, posiciona o simulador minimizado no canto inferior direito
  const computeInitialPosition = () => {
    try {
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      const width = 320;
      const height = 60; // altura aproximada do cabeçalho minimizado
      const margin = 16;
      // Posição inicial: parte inferior central da área útil,
      // para não conflitar com elementos laterais do canvas.
      const x = Math.max(margin, (w - width) / 2);
      const y = Math.max(64, h - height - margin);
      return { x, y };
    } catch {
      return { x: 32, y: 80 };
    }
  };

  useEffect(() => {
    const pos = computeInitialPosition();
    setPosition(pos);
  }, []);

  const protocol = useMemo(() => (sessionId ? String(sessionId) : ""), [sessionId]);
  const isClosedRequest = useMemo(() => {
    const s = normalizeStatusInput(requestStatus);
    return s === 2 || s === 4 || s === 5;
  }, [requestStatus]);

  const focusMessageInput = useCallback(() => {
    if (!expanded || isClosedRequest) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        inputRef.current?.focus?.();
      });
    });
  }, [expanded, isClosedRequest]);

  const refreshRequestDetails = async (id) => {
    if (!id) return;
    try {
      setLoadingRequest(true);
      const dto = await getConversationById(id);
      const status = dto?.status ?? dto?.data?.status ?? null;
      if (status !== null && status !== undefined) {
        setRequestStatus(normalizeStatusInput(status));
      }
    } catch (err) {
      // não trava o simulador se falhar a leitura do status
      console.error("[ChatbotSimulator] Erro ao buscar detalhes da solicitação:", err);
    } finally {
      setLoadingRequest(false);
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setRequestStatus(null);
      return;
    }
    refreshRequestDetails(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const openPopupCall = () => {
    if (!sessionId) return;
    const width = window.screen.width;
    const height = window.screen.height;
    window.open(
      `/popup-call/${sessionId}`,
      "SolicitacaoPopup",
      `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
    );
  };

  const handleStatusChange = (newStatus) => {
    if (!sessionId) return;
    updateMutation.mutate(
      { id: sessionId, status: newStatus },
      {
        onSuccess: async () => {
          setRequestStatus(normalizeStatusInput(newStatus));
          await refreshRequestDetails(sessionId);
        },
        onError: (err) => {
          console.error("[ChatbotSimulator] Erro ao atualizar status:", err);
          const msg =
            err?.message ||
            err?.error ||
            (Array.isArray(err?.message) && err.message[0]) ||
            "Erro ao atualizar status da solicitação.";
          setMessages((prev) => [...prev, { from: "system", text: msg }]);
        },
      }
    );
  };

  const sendMessage = async (text, opts = {}) => {
    const { clearButtonsFromMessageIdx } = opts || {};
    const trimmed = String(text || "").trim();
    if (!trimmed || !userId) return;
    if (isClosedRequest) return;
    if (sendingRef.current) return;

    // Comportamento WhatsApp: ao escolher uma opção, os botões daquela resposta "somem"
    if (typeof clearButtonsFromMessageIdx === "number") {
      setMessages((prev) =>
        prev.map((m, i) =>
          i === clearButtonsFromMessageIdx ? { ...m, buttons: [], linkButtons: [] } : m
        )
      );
    }

    // Mostra a mensagem do usuário imediatamente; o loading passa a ser “digitando…” da resposta
    setMessages((prev) => [...prev, { from: "user", text: trimmed, pending: true }]);
    setSending(true);

    try {
      let sid = sessionId;
      if (!sid) {
        try {
          const res = await fetchData("/chatbot/sessions", "POST", {
            user_id: userId,
            id_projeto: resolveSimulationProjectId() || undefined,
          });
          const data = res?.data ?? res ?? {};
          sid = data.sessionId;
        } catch (createErr) {
          // Sessão anterior travada (409): limpa e tenta de novo; se falhar, reaproveita o protocolo.
          const stuckId = Number(createErr?.data?.id ?? createErr?.id) || 0;
          if (createErr?.status === 409 && stuckId) {
            try {
              await fetchData(`/chatbot/sessions/${stuckId}`, "DELETE");
            } catch {
              /* ignore — segue para retry/reuso */
            }
            try {
              const retry = await fetchData("/chatbot/sessions", "POST", {
                user_id: userId,
                id_projeto: resolveSimulationProjectId() || undefined,
              });
              const retryData = retry?.data ?? retry ?? {};
              sid = retryData.sessionId || stuckId;
            } catch {
              sid = stuckId;
            }
          } else {
            throw createErr;
          }
        }
        setSessionId(sid);
        await refreshRequestDetails(sid);
      }

      const resMsg = await fetchData(`/chatbot/sessions/${sid}/messages`, "POST", { message: trimmed });
      const dataMsg = resMsg?.data ?? resMsg ?? {};
      const turnMessages = buildSimulatorTurnMessages(dataMsg, trimmed);
      const userFromApi = turnMessages.find((m) => m.from === "user");
      const botMessages = turnMessages.filter((m) => m.from !== "user");

      setMessages((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i -= 1) {
          if (next[i]?.from === "user" && next[i]?.pending && next[i]?.text === trimmed) {
            next[i] = {
              ...next[i],
              pending: false,
              slotId: userFromApi?.slotId ?? next[i].slotId ?? null,
            };
            break;
          }
        }
        return botMessages.length ? [...next, ...botMessages] : next;
      });
    } catch (err) {
      const stuckId = Number(err?.data?.id ?? err?.id) || 0;
      if (err?.status === 409 && stuckId && !sessionId) {
        setSessionId(stuckId);
        await refreshRequestDetails(stuckId);
      }
      const msg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        "Erro ao simular mensagem.";
      const hint =
        err?.status === 409 && stuckId
          ? ` Protocolo ${stuckId} — use Limpar ou altere o status para liberar.`
          : "";
      setMessages((prev) => {
        const next = prev.map((m) =>
          m?.pending && m.from === "user" && m.text === trimmed ? { ...m, pending: false } : m
        );
        return [...next, { from: "system", text: `${msg}${hint}` }];
      });
    } finally {
      setSending(false);
      focusMessageInput();
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !userId) return;
    setInput("");
    await sendMessage(text);
  };

  const handleQuickReplyClick = async (replyText, messageIdx) => {
    if (!replyText) return;
    if (!userId || sending || isClosedRequest) return;
    await sendMessage(replyText, { clearButtonsFromMessageIdx: messageIdx });
  };

  const handleOpenMessageSlot = useCallback(
    (message) => {
      const slotId = Number(message?.slotId) || 0;
      if (!slotId || !onNavigateToSlot) return;
      onNavigateToSlot(slotId);
    },
    [onNavigateToSlot]
  );

  useEffect(() => {
    if (!onActiveSlotChange) return;
    onActiveSlotChange(resolveSimulatorActiveSlotId(messages));
  }, [messages, onActiveSlotChange]);

  const pollInactivity = useCallback(async () => {
    if (!sessionId || sendingRef.current) return;
    try {
      const res = await fetchData(`/chatbot/sessions/${sessionId}/inactivity`, "POST", {});
      const data = res?.data ?? res ?? {};
      if (!data.fired) return;
      if (data.encerrado) {
        await refreshRequestDetails(sessionId);
        return;
      }
      const newBotMessages = botMessagesFromUraResponse(data);
      if (newBotMessages.length) {
        setMessages((prev) => [...prev, ...newBotMessages]);
      }
    } catch (err) {
      console.debug("[ChatbotSimulator] poll inatividade:", err?.message || err);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !expanded || isClosedRequest) return undefined;
    const timer = setInterval(pollInactivity, 10000);
    return () => clearInterval(timer);
  }, [sessionId, expanded, isClosedRequest, pollInactivity]);

  const handleReset = async () => {
    const sid = sessionId;
    if (sid) {
      try {
        await fetchData(`/chatbot/sessions/${sid}`, "DELETE");
      } catch (err) {
        console.error("[ChatbotSimulator] Erro ao excluir sessão de simulação:", err);
      }
    }
    setSessionId(null);
    setMessages([]);
    setInput("");
    setRequestStatus(null);
    onActiveSlotChange?.(null);
  };

  const handleMouseDown = (e) => {
    // Evita iniciar drag ao clicar em botões/inputs
    if (e.target instanceof HTMLElement && e.target.closest("button, input, textarea")) {
      return;
    }
    e.preventDefault();
    setDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleDragMove = (clientX, clientY) => {
    setPosition({
      x: Math.max(8, clientX - offset.x),
      y: Math.max(64, clientY - offset.y),
    });
  };

  // listeners globais para não "perder" o drag quando o mouse sai do Paper
  useEffect(() => {
    if (!dragging) return undefined;

    const onMove = (e) => {
      e.preventDefault();
      handleDragMove(e.clientX, e.clientY);
    };

    const onUp = () => {
      setDragging(false);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, offset.x, offset.y]);

  return (
    <Paper
      elevation={1}
      sx={{
        position: "fixed",
        // Mantém o simulador acima do conteúdo, mas abaixo de Dialogs/Drawers (z-index padrão 1300),
        // para que, ao abrir popups, ele também fique escurecido pelo backdrop.
        zIndex: 1100,
        top: position.y,
        right: "auto",
        left: position.x,
        p: 1.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        width: 320,
        maxWidth: "90vw",
        height: expanded ? 420 : "auto",
        maxHeight: 480,
        overflow: expanded ? "hidden" : "visible",
        cursor: dragging ? "grabbing" : "default",
        boxShadow: (t) => t.shadows[12],
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
          cursor: "grab",
          userSelect: dragging ? "none" : "auto",
          gap: 1,
        }}
        onMouseDown={handleMouseDown}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Teste o ChatBot
        </Typography>
        <Button
          size="small"
          onClick={handleReset}
          sx={{ ml: "auto", textTransform: "none" }}
          disabled={!messages.length && !sessionId}
        >
          Limpar
        </Button>
        <Button
          size="small"
          onClick={() => {
            setExpanded((prev) => {
              const next = !prev;
              if (!prev && next) {
                // Ao expandir pela primeira vez, garante que o popup caiba inteiro na tela,
                // abrindo visualmente "para cima", mas sem cortar.
                try {
                  const h = window.innerHeight || 0;
                  const popupHeight = 420; // mesma altura usada no sx
                  const margin = 16;
                  const targetY = Math.max(64, h - popupHeight - margin);
                  setPosition((p) => ({
                    ...p,
                    y: targetY,
                  }));
                } catch {
                  // ignore em ambientes sem window
                }
              } else if (prev && !next) {
                // Ao minimizar, volta para a posição inicial calculada
                const initial = computeInitialPosition();
                setPosition(initial);
              }
              return next;
            });
          }}
          sx={{ ml: 1, textTransform: "none" }}
        >
          {expanded ? "Minimizar" : "Expandir"}
        </Button>
      </Box>
      {expanded && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              px: 0.5,
              flexShrink: 0,
            }}
          >
            {!sessionId ? (
              <Typography variant="body2" color="text.secondary">
                Ao iniciar o teste, o simulador cria automaticamente uma <b>solicitação</b> (simulação) para este ChatBot.
                Depois de criada, você verá aqui o <b>protocolo</b> e poderá abrir em um <b>popup</b> e alterar o <b>status</b>.
              </Typography>
            ) : (
              <>
                <Tooltip title={`Abrir solicitação ${protocol} em popup`}>
                  <Chip
                    clickable
                    onClick={openPopupCall}
                    label={`Protocolo: ${protocol}`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      maxWidth: 160,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Tooltip>

                <IconButton
                  size="small"
                  onClick={openPopupCall}
                  title="Abrir solicitação em popup"
                  sx={{ ml: -0.5 }}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>

                {requestStatus !== null && requestStatus !== undefined && (
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    <StatusChipSelector
                      currentStatus={requestStatus}
                      onStatusChange={handleStatusChange}
                      size="small"
                    />
                    {loadingRequest && (
                      <Typography variant="caption" color="text.secondary">
                        Atualizando…
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>

          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              mb: 1,
              px: 0.5,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 3 },
            }}
          >
            {messages.map((m, idx) => {
              const alignment = m.from === "user" ? "flex-end" : "flex-start";
              const hasButtons = Array.isArray(m.buttons) && m.buttons.length > 0;
              const hasLinkButtons = Array.isArray(m.linkButtons) && m.linkButtons.length > 0;
              const hasAttachment = Boolean(m.attachment?.url);

              return (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: alignment,
                    mt: 0.5,
                  }}
                >
                  {hasAttachment && (
                    <Box sx={{ maxWidth: "78%", mb: m.text || hasButtons ? 0.5 : 0 }}>
                      <AgentSimulatorAttachment attachment={m.attachment} isOwn={false} />
                    </Box>
                  )}

                  {(m.text != null && String(m.text).trim() !== "") && (
                    <Tooltip
                      title={
                        m.slotId && onNavigateToSlot
                          ? `Abrir passo #${m.slotId} no editor`
                          : m.from === "system"
                            ? ""
                            : "Passo não identificado nesta mensagem"
                      }
                      disableHoverListener={m.from === "system"}
                    >
                      <Box
                        onClick={() => handleOpenMessageSlot(m)}
                        role={m.slotId && onNavigateToSlot ? "button" : undefined}
                        tabIndex={m.slotId && onNavigateToSlot ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (!m.slotId || !onNavigateToSlot) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleOpenMessageSlot(m);
                          }
                        }}
                        sx={{
                          maxWidth: "78%",
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 2,
                          bgcolor:
                            m.from === "user"
                              ? "primary.main"
                              : m.from === "bot"
                              ? "grey.200"
                              : "warning.light",
                          color: m.from === "user" ? "primary.contrastText" : "text.primary",
                          fontSize: "0.85rem",
                          whiteSpace: "pre-line",
                          opacity: m.pending ? 0.85 : 1,
                          cursor: m.slotId && onNavigateToSlot ? "pointer" : "default",
                          transition: "box-shadow 0.15s ease, transform 0.15s ease",
                          "&:hover":
                            m.slotId && onNavigateToSlot
                              ? {
                                  boxShadow: 2,
                                  transform: "translateY(-1px)",
                                }
                              : undefined,
                          "&:focus-visible":
                            m.slotId && onNavigateToSlot
                              ? {
                                  outline: "2px solid",
                                  outlineColor: "primary.main",
                                  outlineOffset: 2,
                                }
                              : undefined,
                        }}
                      >
                        {m.text}
                      </Box>
                    </Tooltip>
                  )}

                  {(hasButtons || hasLinkButtons) && m.from === "bot" ? (
                    <Box
                      sx={{
                        width: "78%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        mt: m.text ? 0.5 : 0,
                      }}
                    >
                      {m.buttons.map((b, bIdx) => {
                        const opt = Array.isArray(m.buttonOptions) ? m.buttonOptions[bIdx] : null;
                        const sendValue =
                          (opt?.id && String(opt.id).trim()) ||
                          (typeof b === "object" ? b?.id || b?.label : b) ||
                          "";
                        const label = typeof b === "object" ? b?.label || b?.title || sendValue : b;
                        return (
                          <Button
                            key={`${idx}-reply-${bIdx}-${label}`}
                            variant="outlined"
                            size="small"
                            onClick={() => handleQuickReplyClick(sendValue, idx)}
                            disabled={sending || isClosedRequest}
                            sx={{
                              textTransform: "none",
                              justifyContent: "center",
                              borderRadius: 2,
                              bgcolor: "background.paper",
                            }}
                          >
                            {label}
                          </Button>
                        );
                      })}
                      {m.linkButtons.map((linkBtn, bIdx) => (
                        <Button
                          key={`${idx}-link-${bIdx}-${linkBtn.url}`}
                          variant="outlined"
                          size="small"
                          href={linkBtn.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            textTransform: "none",
                            justifyContent: "center",
                            borderRadius: 2,
                            bgcolor: "background.paper",
                          }}
                        >
                          {linkBtn.label || "Abrir link"}
                        </Button>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
            {sending && <SimulatorTypingBubble label="Gerando resposta…" />}
            {messages.length === 0 && !sending && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Digite uma mensagem abaixo para testar o fluxo do ChatBot atribuído a este usuário. As respostas usam o
                mesmo fluxo aplicado às conversas reais.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", mt: 0.5, flexShrink: 0, gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                inputRef={inputRef}
                size="small"
                placeholder={
                  isClosedRequest
                    ? "Solicitação encerrada — não é possível enviar mensagens."
                    : sending
                      ? "Aguardando a resposta…"
                      : "Digite uma mensagem..."
                }
                fullWidth
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={!userId || sending || isClosedRequest}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (!isClosedRequest && !sending) handleSend();
                  }
                }}
              />
              <IconButton
                sx={{ ml: 1 }}
                color="primary"
                onClick={handleSend}
                disabled={!userId || sending || isClosedRequest || !input.trim()}
              >
                <SendIcon />
              </IconButton>
            </Box>
            {sending && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, fontStyle: "italic" }}>
                Mensagem enviada. Aguardando resposta do fluxo / agente…
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
}

